import { useMemo, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, ViewerContext } from "dash-ui";
import { SubscriptionProduct } from "gql/generated.graphql";
import {
  SubscriptionProductNamesEnum,
  ProductSubscriptionStatusesEnum,
} from "dash-common";
import { DEMO_BOOKING_URL } from "../../SubscriptionComponents.constants";
import { LinkToCustomerPortal } from "features/update-payment-method";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";
import { getRemainingTrialDays } from "../SubscriptionPlanCardV3.helpers";

const REST_API_DOMAIN = process.env["REACT_APP_REST"];

// ============================================================
// SUB-COMPONENTS
// ============================================================

interface InvertedActionButtonProps {
  colors: {
    bgColor: string;
    textColor: string;
  };
  onClick: () => void;
  label: string;
}

function InvertedActionButton({
  colors,
  onClick,
  label,
}: InvertedActionButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      className="w-full mb-3 rounded-md font-semibold transition-all duration-200 text-sm border-2 hover:scale-[1.02] hover:shadow-md"
      style={{
        borderColor: colors.textColor,
        color: isHovered ? colors.bgColor : colors.textColor,
        height: "40px",
        backgroundColor: isHovered ? colors.textColor : "transparent",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

interface ICardActionsProps {
  isCurrent?: boolean;
  isTrialing: boolean;
  userProduct?: SubscriptionProduct | null;
  buttonText: string;
  colors: {
    bgColor: string;
    titleColor: string;
    textColor: string;
    priceColor: string;
    buttonBg: string;
    buttonBgHover: string;
    buttonText: string;
    bookDemoColor: string;
  };
  loading?: boolean;
  subscription: any;
  onSelectPlan: (product: SubscriptionProduct, withTrial?: boolean) => void;
  product: SubscriptionProduct;
  canUserTrial?: boolean;
  gtmProductTag: string;
  trackingClassName: string;
  isBasicTier: boolean;
  handleCancelTrial: () => void;
  showManageLink?: boolean;
  onRenewPlan?: () => void;
}

export default function CardActions({
  isCurrent,
  isTrialing,
  userProduct,
  buttonText,
  colors,
  loading,
  subscription,
  onSelectPlan,
  product,
  canUserTrial,
  gtmProductTag,
  trackingClassName,
  isBasicTier,
  handleCancelTrial,
  showManageLink,
  onRenewPlan,
}: ICardActionsProps) {
  const { account } = useContext(ViewerContext);
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialRevert",
  });
  const { t: tc } = useTranslation("pages", {
    keyPrefix: "subscriptionPlans",
  });

  // ============================================================
  // DERIVED STATE - Calculate all display conditions upfront
  // ============================================================

  // Subscription state flags
  const hasScheduledDowngrade = !!subscription?.schedule;
  const isScheduledCancellation =
    subscription?.status ===
    ProductSubscriptionStatusesEnum.SCHEDULED_CANCELLATION;

  // A cancelled sub is only trial-related if the trial period itself is still running.
  // Without this check, any cancelled paid plan would render the trial UI.
  const isScheduledTrialCancellation =
    isScheduledCancellation &&
    !!subscription?.trialEndAt &&
    new Date(subscription.trialEndAt).getTime() > Date.now();
  const isScheduledPaidCancellation =
    isScheduledCancellation && !isScheduledTrialCancellation;
  const isCurrentScheduledPaidCancellation =
    isCurrent && isScheduledPaidCancellation;

  // Current plan display states
  const isCurrentAndActive =
    isCurrent &&
    !isTrialing &&
    !isScheduledTrialCancellation &&
    !isScheduledPaidCancellation;
  const isCurrentAndTrialing =
    isCurrent && (isTrialing || isScheduledTrialCancellation);

  // Calculate remaining trial days for display
  const remainingTrialDays = useMemo(
    () =>
      (isTrialing || isScheduledTrialCancellation) &&
      subscription?.currentPeriodEndAt
        ? getRemainingTrialDays(subscription.currentPeriodEndAt)
        : null,
    [isTrialing, isScheduledTrialCancellation, subscription?.currentPeriodEndAt]
  );

  const remainingSubscriptionDays = useMemo(
    () =>
      isCurrentScheduledPaidCancellation && subscription?.currentPeriodEndAt
        ? getRemainingTrialDays(subscription.currentPeriodEndAt)
        : null,
    [isCurrentScheduledPaidCancellation, subscription?.currentPeriodEndAt]
  );

  // Action button configuration for non-current plans
  const isApiProduct = product.name === SubscriptionProductNamesEnum.API;
  const actionButtonText = isApiProduct ? tc("contactSales") : tc("bookDemo");
  const actionButtonUrl = isApiProduct
    ? "mailto:info@dashtodash.com?subject=DASH API Inquiry"
    : DEMO_BOOKING_URL;

  // Manage subscription link visibility (only for current paid plans, not trialing non-basic)
  const isManageLinkVisible =
    isCurrent &&
    !(
      isTrialing &&
      userProduct?.name !== SubscriptionProductNamesEnum.BASIC &&
      userProduct?.name !== SubscriptionProductNamesEnum.DIRECT &&
      userProduct?.name !== SubscriptionProductNamesEnum.EXCHANGE
    ) &&
    product.price !== 0;

  // ============================================================
  // HANDLERS
  // ============================================================

  const openCustomerPortal = () => {
    const portalUrl = `${REST_API_DOMAIN}/stripe/create-portal-session?customerId=${account?.stripeCustomerId}`;
    window.location.href = portalUrl;
  };

  const isTrialRevertScheduled = isTrialing && hasScheduledDowngrade;
  const canCancelScheduledDowngrade =
    hasScheduledDowngrade && !isTrialRevertScheduled && !!onRenewPlan;
  const isScheduledPlan =
    !!subscription?.schedule?.nextPhasePriceId &&
    product.stripePriceId === subscription.schedule.nextPhasePriceId;
  const scheduledPlanStart =
    subscription?.schedule?.nextPhaseStart || subscription?.currentPeriodEndAt;

  // A trialing subscription that already has a schedule is heading back to
  // another plan when the trial ends; cancelling it again would be rejected,
  // so the only action left is to undo the schedule.
  const trialAction = isScheduledTrialCancellation
    ? { label: tc("reactivateTrial"), onClick: openCustomerPortal }
    : hasScheduledDowngrade
    ? onRenewPlan && { label: t("undoButton"), onClick: onRenewPlan }
    : { label: tc("cancelTrial"), onClick: handleCancelTrial };

  // ============================================================
  // RENDER LOGIC
  // Decision tree for what to display:
  //
  // NON-CURRENT PLANS:
  //   - Has scheduled downgrade → "Cancel Scheduled Downgrade" button
  //   - No scheduled downgrade → Action button (Subscribe/Upgrade/Get)
  //   - Not basic tier → "Book a Demo" / "Contact Sales" button
  //
  // CURRENT PLANS:
  //   - Active (not trial) → "This is your current plan." text
  //   - Trialing → "Cancel trial" button (or "Undo trial cancellation" once a
  //     schedule exists) + trial countdown
  //   - Scheduled cancellation of a trial → "Reactivate Trial" button + trial countdown
  //   - Scheduled cancellation of a paid sub → "Reactivate Subscription" button + "ends in N days"
  //   - "Manage or Cancel Subscription" link (if showManageLink)
  // ============================================================

  return (
    <div className="px-5 pb-6 pt-0" style={{ backgroundColor: colors.bgColor }}>
      {/* ========== PRIMARY ACTION AREA ========== */}

      {/* NON-CURRENT: trial going back to another plan (no actions) */}
      {!isCurrent && isTrialRevertScheduled && (
        <div
          className="w-full mb-3 flex items-center justify-center text-sm font-medium opacity-80"
          style={{ height: "40px", color: colors.textColor }}
        >
          {isScheduledPlan &&
            scheduledPlanStart &&
            t("planFromDate", { date: formatDateString(scheduledPlanStart) })}
        </div>
      )}

      {/* NON-CURRENT: Cancel Scheduled Downgrade button */}
      {!isCurrent && canCancelScheduledDowngrade && (
        <button
          className="w-full mb-3 rounded-md font-semibold transition-colors duration-200 text-sm hover:opacity-90 flex items-center justify-center gap-2"
          style={{
            backgroundColor: colors.buttonBg,
            color: colors.buttonText,
            height: "40px",
          }}
          onClick={onRenewPlan}
        >
          {tc("cancelScheduledDowngrade")}
        </button>
      )}

      {/* NON-CURRENT: Regular action button (Subscribe/Upgrade/Get) */}
      {!isCurrent && !hasScheduledDowngrade && (
        <button
          className={`w-full mb-3 rounded-md font-semibold transition-colors duration-200 text-sm hover:opacity-90 flex items-center justify-center gap-2 ${gtmProductTag} ${trackingClassName}`}
          style={{
            backgroundColor: colors.buttonBg,
            color: colors.buttonText,
            height: "40px",
          }}
          disabled={loading}
          onClick={() => onSelectPlan(product, canUserTrial)}
          aria-disabled={loading || !!subscription?.schedule}
        >
          {loading && <Spinner size={16} color={colors.buttonText} />}
          {buttonText}
        </button>
      )}

      {/* CURRENT: Active plan indicator */}
      {isCurrentAndActive && (
        <div
          className="w-full mb-3 flex items-center justify-center text-md font-medium"
          style={{
            height: "40px",
            color: colors.textColor,
          }}
        >
          {tc("currentPlanNote")}
        </div>
      )}

      {/* CURRENT: Trial action button (Cancel trial / Reactivate Trial) */}
      {isCurrentAndTrialing && trialAction && (
        <InvertedActionButton
          colors={colors}
          onClick={trialAction.onClick}
          label={trialAction.label}
        />
      )}

      {/* CURRENT: Paid subscription scheduled for cancellation */}
      {isCurrentScheduledPaidCancellation && (
        <InvertedActionButton
          colors={colors}
          onClick={openCustomerPortal}
          label={tc("reactivateSubscription")}
        />
      )}

      {/* ========== SECONDARY ACTION AREA ========== */}

      {/* NON-CURRENT & NON-BASIC: Book a Demo / Contact Sales */}
      {!isCurrent && !isBasicTier && (
        <button
          className="w-full text-center text-sm font-medium rounded-md transition-colors duration-200 bg-transparent hover:bg-white/20"
          style={{
            color: colors.bookDemoColor,
            height: "40px",
          }}
          onClick={() => {
            window.open(actionButtonUrl, "_blank");
          }}
        >
          {actionButtonText}
        </button>
      )}

      {/* CURRENT or BASIC: Spacer with optional countdown */}
      {(isCurrent || isBasicTier) && (
        <div
          className="w-full text-center text-xs font-medium flex items-center justify-center"
          style={{ height: "40px", color: colors.textColor }}
        >
          {isCurrentAndTrialing && remainingTrialDays !== null && (
            <>{tc("daysLeftInTrial", { count: remainingTrialDays })}</>
          )}
          {isCurrentScheduledPaidCancellation &&
            remainingSubscriptionDays !== null && (
              <>{tc("accessExpiresIn", { count: remainingSubscriptionDays })}</>
            )}
        </div>
      )}

      {/* ========== FOOTER LINK ========== */}

      {/* CURRENT: Manage subscription link (billing page only) */}
      {showManageLink && (
        <div className="flex flex-col items-center">
          <LinkToCustomerPortal
            className={`${
              !isManageLinkVisible ? "invisible" : ""
            } text-md underline underline-offset-2 text-center`}
            style={{ color: colors.textColor }}
            source="subscription_plan_card"
          >
            {isScheduledCancellation
              ? tc("manageOrResubscribe")
              : tc("manageOrCancel")}
          </LinkToCustomerPortal>
          {/* DSH-837: warn API users that cancelling deactivates their API
              access. Via the Stripe portal the cancellation takes effect at the
              end of the billing period (cancel_at_period_end), so the keys stay
              live until then; they are deactivated, not deleted. */}
          {isApiProduct && isManageLinkVisible && (
            <p
              className="mt-2 text-center text-xs"
              style={{ color: colors.textColor }}
            >
              {tc("apiCancelNote")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
